<template>
  <div class="flex items-center justify-between p-2 sm:p-4 relative group rounded-b-lg overflow-y-visible"
    @mouseenter="showHoverButtons = true" @mouseleave="showHoverButtons = false" @click="$emit('listclick')"
    :class="[bgClass, cursorClass]">

    <!-- Left Icon (Radio Buttons) -->
    <div v-if="editmode" class="flex-shrink-0 mr-4 text-skin-b-list-mute">
      <component :is="itemdata.checked ? 'IconRadioChecked' : 'IconRadioUnchecked'" class="m-2 w-8 h-8" />
    </div>

    <!-- Left Icon (Main Icon based on type) -->
    <div class="flex-shrink-0 mr-4 rounded-lg border border-slate-300 bg-[#6B7280]">
      <p v-if="itemdata.alias" class="w-12 h-12 px-1 py-2.5 text-center">
        <span class="text-lg font-semibold text-white">{{ itemdata.alias }}</span>
      </p>
      <IconPerson v-else class="m-2 w-8 h-8 text-white"></IconPerson>
    </div>

    <!-- Middle Section: Title and Status -->
    <div class="flex-grow">
      <div class="mb-1 sm:mb-2">
        <strong>
          {{ itemdata.name }}
        </strong>
        &nbsp;
        <ListItemBadge @click="$emit('toggle-showmore')" class="cursor-pointer" v-if="itemdata.rfidtbl.length>0" :uppercase="false">{{
          itemdata.rfidtbl.length }} RFIDs
        </ListItemBadge>

        <ListItemBadge v-if="statusBadge" :color="statusBadge.color">{{ statusBadge.label }}</ListItemBadge>
      </div>
      <div class="text-skin-b-mute">
        <span v-if="itemdata.email">
          <IconEmail class="inline-block -mt-1 w-5 h-5" />
          &nbsp;{{ itemdata.email }}
          <div class="hidden sm:inline-block cursor-pointer" @click.stop="copyToClipBoard(itemdata.email)">
            <IconCopy class="inline-block -mt-1 w-5 h-5" />
          </div>
        </span>
        <span v-if="itemdata.reference" class="hidden sm:inline-block">
          &nbsp;&nbsp;&#8231;&nbsp;
          <IconNote class="inline-block -mt-1 w-5 h-5 text-skin-b-mute" />
          {{ itemdata.reference }}
        </span>
         <!--       
        <span v-if="itemdata.info" class="hidden sm:inline-block text-skin-b-list-mute">
          &nbsp;&nbsp;&#8231;&nbsp;
          <IconInfo class="inline-block -mt-1 w-5 h-5" />
          {{ itemdata.info }}
        </span>

        <span class="hidden sm:inline-block text-skin-b-list-mute">
          &nbsp;&nbsp;&#8231;&nbsp;
          <IconRfidAdded class="inline-block -mt-1 w-5 h-5" />
          {{ itemdata.creationoffset }}
        </span>
        -->
      </div>
      <div v-if="itemdata.showmore" class="mt-2">
        <ListItemBadge v-for="rfid in itemdata.rfidtbl" :key="rfid.id" :uppercase="false" :color="rfid.status=='disabled' ? 'red': rfid.typ.includes('fc_rfid') ? 'amber' : 'default'">
          <IconRfidToken class="inline-block w-4 h-4" />
          {{ rfid.info }}
        </ListItemBadge>
      </div>
    </div>

    <!-- Right Buttons -->
    <div v-if="!editmode" class="flex items-center relative">
      <div v-if="showHoverButtons && !isDropdownVisible"
        class="hidden absolute right-11 top-1/2 transform -translate-y-1/2 z-10 sm:flex sm:items-center">
        <ButtonSmallIcon v-for="(btn, index) in filteredHoverButtons" :key="index" :border="index === 0 ? 'left' : ''"
          :hovercolor="btn.color" @click="btn.onClick">
          <component :is="btn.icon" class="w-6 h-6" />
        </ButtonSmallIcon>
      </div>

      <!-- Dropdown Button -->
      <div class="relative">
        <button type="button"
          class="inline-flex items-center px-2 py-1 border-2 border-transparent focus:ring-0 focus:ring-transparent"
          :class="dropdownClass" @click.stop="toggleDropdown">
          <component :is="isDropdownVisible ? 'IconClose' : 'Icon3Dots'" class="w-6 h-6" />
        </button>
        <ListItemDropdown v-if="isDropdownVisible" class="top-full z-50" :items="filteredHoverButtons"
          @close="isDropdownVisible = false" />
      </div>
    </div>
  </div>
</template>

<script>
import ListItemDropdown from './ListItemDropdown.vue'
import ListItemBadge from './ListItemBadge.vue'
import ButtonSmallIcon from './ButtonSmallIcon.vue'
import Icon3Dots from '../icons/Icon3Dots.vue'
import IconRadioChecked from '../icons/IconRadioChecked.vue'
import IconRadioUnchecked from '../icons/IconRadioUnchecked.vue'
import IconPerson from '../icons/IconPerson.vue'
import IconEmail from '../icons/IconEmail.vue'
import IconInfo from '../icons/IconInfo.vue'
import IconLockCable from '../icons/IconLockCable.vue'
import IconLearnRfid from '../icons/IconLearnRfid.vue'
import IconSolarPower from '../icons/IconSolarPower.vue'
import IconRfidToken from '../icons/IconRfidToken.vue'
import IconNote from '../icons/IconNote.vue'
import IconStacks from '../icons/IconStacks.vue'
import IconCopy from '../icons/IconCopy.vue'
import IconRfidAdded from '../icons/IconRfidAdded.vue'
import IconEdit from '../icons/IconEdit.vue'
import IconAddRfid from '../icons/IconAddRfid.vue'
import IconShare from '../icons/IconShare.vue'
import IconDelete from '../icons/IconDelete.vue'
import IconBlock from '../icons/IconBlock.vue'
import IconCheck from '../icons/IconCheck.vue'
import IconClose from '../icons/IconClose.vue'
import { useI18n } from 'vue-i18n'

export default {
  setup() {
    const { t } = useI18n();
    return { t };
  },
  components: { ListItemDropdown, ListItemBadge, ButtonSmallIcon, 
                Icon3Dots, IconRadioChecked, IconRadioUnchecked,
                IconPerson, IconEmail, IconInfo, IconLockCable, IconLearnRfid, IconSolarPower,
                IconNote, IconRfidToken, IconCopy, IconRfidAdded, IconStacks, IconAddRfid,
                IconEdit, IconShare, IconDelete, IconBlock, IconCheck, IconClose, 
              },
  emits: ['listclick', 'toggle-showmore'],
  props: {
    editmode: Boolean,
    itemdata: { type: Object, default: () => ({}) },
    hoverButtons: { type: Array, default: () => [] },
  },
  data() {
    return {
      isDropdownVisible: false,
      showHoverButtons: false,
    };
  },
  computed: {
    bgClass() {
      return (this.editmode && this.itemdata.checked) || this.isDropdownVisible ? 'bg-skin-active128/5' : '';
    },
    cursorClass() {
      return this.editmode ? 'cursor-pointer hover:bg-skin-active128/5' : '';
    },
    iconBgClass() {
      return (this.editmode && this.itemdata.checked) || this.isDropdownVisible ? 'bg-skin-active128/10' : 'bg-slate-100';
    },
    dropdownClass() {
      return this.showHoverButtons && !this.isDropdownVisible ? 'sm:text-skin-b-list-base sm:bg-skin-b-list-back sm:hover:bg-skin-b-back border-2 sm:border-skin-b-list-bord sm:rounded-r-md' : 'text-skin-b-list-mute bg-transparent';
    },
    iconComponent() {
      const iconMap = {
        rfid: 'IconCar',
        rfid_checkin: 'IconIdCard',
        rfid_user: 'IconGroups',
        rfid_local: 'IconCar',
        fc_rfid_lock: 'IconLockCable',
        fc_rfid_pair: 'IconLearnRfid',
        fc_rfid_charge: 'IconSolarPower',
      };
      return iconMap[this.itemdata.typ] || 'IconPerson';
    },
    itemLabels() {
      return {
        rfid_checkin: this.$t('pairing.itemeci'),
        rfid_local: this.$t('pairing.itemlocal'),
        fc_rfid_charge: this.$t('pairing.itemcharge'),
        fc_rfid_lock: this.$t('pairing.itemlock'),
        fc_rfid_pair: this.$t('pairing.itemlearn'),
      };
    },
    statusBadge() {
      if (!this.itemdata.status||this.itemdata.status=='active') return null;
      const statusMap = {
        //active: { color: 'green', label: this.$t('pairing.active') },
        disabled: { color: 'red', label: this.$t('pairing.deactive') },
      };
      return statusMap[this.itemdata.status];
    },
    filteredHoverButtons() {
      return this.hoverButtons.filter(button => button.show);
    },
  },
  methods: {
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },
    copyToClipBoard(textToCopy) {
      navigator.clipboard.writeText(textToCopy);
    },
  },
};
</script>
